import { createStepper, setButtonState } from './fa-resin-impuls.js';

// The theme owns the Shopify request. This adapter only observes its result and
// prevents a second Resin form for the same product from submitting meanwhile.
const forms = new WeakMap();
const pendingProducts = new Map();
const confirmationDelay = 30000;
let statusIndex = 0;
const labels = JSON.parse(document.querySelector('[data-fr2-translations]')?.textContent || '{}');

function selection(controller) {
  return {
    variant: controller.form.querySelector('[name="id"]')?.value || '',
    quantity: controller.form.querySelector('[name="quantity"]')?.value || '1',
  };
}

function available(controller) {
  const variant = controller.form.querySelector('[name="id"]');
  const option = controller.form.querySelector('[data-fr2-variant]')?.selectedOptions[0];
  return Boolean(variant && !variant.disabled && option?.dataset.available !== 'false');
}

function render(controller, state, customLabels = {}) {
  controller.state = state;
  for (const button of controller.buttons) {
    setButtonState(button, state, { ...labels, ...customLabels });
    const title = button.dataset.fr2ProductTitle;
    if (title) button.setAttribute('aria-label', `${title} – ${button.querySelector('.fi-button__label').textContent}`);
  }
}

function announce(controller, message = '', tone = '') {
  controller.status.dataset.tone = tone;
  controller.status.setAttribute('role', tone === 'error' ? 'alert' : 'status');
  controller.status.setAttribute('aria-live', tone === 'error' ? 'assertive' : 'polite');
  controller.status.textContent = message;
  controller.status.hidden = !message;
}

function reset(controller) {
  render(controller, available(controller) ? 'idle' : 'disabled');
  announce(controller);
  controller.steppers.forEach((stepper) => stepper.refresh());
}

function lockFields(controller, operation) {
  if (operation.fields.has(controller)) return;
  const fields = [...controller.form.querySelectorAll('input, select, textarea')].map((field) => ({
    field, value: field.value,
    disabled: field.hasAttribute('data-fr2-lock-disabled') ? field.dataset.fr2LockDisabled === 'true' : field.disabled,
    readOnly: field.hasAttribute('data-fr2-lock-readonly') ? field.dataset.fr2LockReadonly === 'true' : field.readOnly,
  }));
  operation.fields.set(controller, fields);
  // The native handler builds FormData synchronously. Do not disable named
  // fields before it has captured the intended variant, quantity and properties.
  queueMicrotask(() => {
    if (pendingProducts.get(controller.key) !== operation) return;
    for (const snapshot of fields) {
      if (snapshot.field instanceof HTMLSelectElement) {
        snapshot.field.dataset.fr2LockDisabled = String(snapshot.disabled);
        snapshot.field.disabled = true;
      } else if (snapshot.field.type !== 'hidden') {
        snapshot.field.dataset.fr2LockReadonly = String(snapshot.readOnly);
        snapshot.field.readOnly = true;
      }
    }
    controller.steppers.forEach((stepper) => stepper.refresh());
  });
}

function joinOperation(controller, operation) {
  lockFields(controller, operation);
  if (!available(controller)) return;
  const current = selection(controller);
  const sameSelection = current.variant === operation.selection.variant && current.quantity === operation.selection.quantity;
  render(controller, operation.uncertain ? 'disabled' : 'loading', operation.uncertain
    ? { disabled: labels.pending }
    : { loading: sameSelection ? labels.loading : labels.wait });
  if (controller === operation.source) announce(controller, labels.adding);
}

function init(component) {
  if (forms.has(component)) return forms.get(component);
  const form = component.querySelector('form');
  const buttons = [...component.querySelectorAll('button[type="submit"].fi-button')];
  if (!form || !buttons.length || buttons.some((button) => !button.querySelector('.fi-button__label'))) return null;
  // Review dialogs clone cards, so stale status nodes and IDs must not survive.
  component.querySelector('[data-fr2-impuls-status]')?.remove();
  const status = document.createElement('p');
  status.id = `fr2-impuls-status-${++statusIndex}`;
  status.className = 'fr2-impuls-status fi-status';
  status.dataset.fr2ImpulsStatus = '';
  status.setAttribute('aria-atomic', 'true');
  status.hidden = true;
  form.append(status);
  for (const nativeStatus of component.querySelectorAll('[ref="liveRegion"], [ref="addToCartTextError"]')) {
    nativeStatus.hidden = true;
    nativeStatus.setAttribute('aria-hidden', 'true');
    nativeStatus.setAttribute('aria-live', 'off');
  }
  for (const button of buttons) {
    const described = (button.getAttribute('aria-describedby') || '').split(/\s+/)
      .filter((id) => id && !id.startsWith('fr2-impuls-status-'));
    button.setAttribute('aria-describedby', [...described, status.id].join(' '));
  }
  const controller = {
    component, form, buttons, status, state: 'idle',
    key: component.dataset.productId || form.querySelector('[name="id"]')?.value || form.id,
    steppers: [...component.querySelectorAll('.fi-stepper')].map((root) => createStepper(root, { errorMessage: labels.quantity_error })),
  };
  forms.set(component, controller);
  const operation = pendingProducts.get(controller.key);
  if (operation) joinOperation(controller, operation);
  else reset(controller);
  return controller;
}

function initWithin(scope) {
  if (scope.matches?.('.fr2-product-form')) init(scope);
  scope.querySelectorAll?.('.fr2-product-form').forEach(init);
}

function stop(event) {
  event.preventDefault();
  event.stopImmediatePropagation();
}

// The theme delegates submit at document capture. Window capture therefore
// guards repeated actions before that handler, including Enter/programmatic submit.
window.addEventListener('submit', (event) => {
  const component = event.target.closest?.('.fr2-product-form');
  if (!component) return;
  const controller = init(component);
  if (!controller) return;
  if (pendingProducts.has(controller.key) || !available(controller) ||
      controller.buttons.some((button) => button.disabled)) return stop(event);
  if (controller.steppers.some((stepper) => !stepper.validate()) || !controller.form.reportValidity()) {
    stop(event);
    controller.form.reportValidity();
    return;
  }
  const operation = { source: controller, selection: selection(controller), fields: new Map(), uncertain: false };
  pendingProducts.set(controller.key, operation);
  document.querySelectorAll('.fr2-product-form').forEach((peer) => {
    const candidate = init(peer);
    if (candidate?.key === controller.key) joinOperation(candidate, operation);
  });
  operation.timeout = window.setTimeout(() => {
    if (pendingProducts.get(controller.key) !== operation) return;
    // A missing theme event does not establish whether Shopify accepted the add.
    // Keep the lock: a blind retry could add the same quantity twice.
    operation.uncertain = true;
    for (const peer of operation.fields.keys()) {
      if (available(peer)) render(peer, 'disabled', { disabled: labels.pending });
    }
    announce(controller, labels.delayed, 'error');
    const cart = document.createElement('a');
    cart.href = window.Theme?.routes?.cart_url || labels.cart_url || '/cart';
    cart.textContent = labels.check;
    cart.className = 'fi-button fi-button--quiet';
    controller.status.append(' ', cart);
  }, confirmationDelay);
}, true);

function finish(event, isError) {
  const component = event.target.closest?.('.fr2-product-form');
  const controller = component && forms.get(component);
  if (!controller) return;
  const operation = pendingProducts.get(controller.key);
  if (!operation || operation.source !== controller) return;
  if (!isError && event.detail?.data?.source !== 'product-form-component') return;
  if (!isError && event.detail?.data?.didError) return;
  pendingProducts.delete(controller.key);
  window.clearTimeout(operation.timeout);
  const details = event.detail?.data;
  const error = typeof details?.description === 'string' ? details.description :
    typeof details?.message === 'string' ? details.message : labels.failed;
  for (const [peer, snapshots] of operation.fields) {
    for (const snapshot of snapshots) {
      snapshot.field.disabled = snapshot.disabled;
      if (typeof snapshot.readOnly === 'boolean') snapshot.field.readOnly = snapshot.readOnly;
      delete snapshot.field.dataset.fr2LockDisabled;
      delete snapshot.field.dataset.fr2LockReadonly;
    }
    const current = selection(peer);
    const sameSelection = current.variant === operation.selection.variant && current.quantity === operation.selection.quantity;
    render(peer, !available(peer) ? 'disabled' : sameSelection ? (isError ? 'error' : 'success') : 'idle');
    announce(peer, peer === controller ? (isError ? error : labels.added) : '', isError ? 'error' : 'success');
    peer.steppers.forEach((stepper) => stepper.refresh());
  }
}

document.addEventListener('cart:update', (event) => finish(event, false), true);
document.addEventListener('cart:error', (event) => finish(event, true), true);

for (const name of ['input', 'change']) window.addEventListener(name, (event) => {
  const component = event.target.closest?.('.fr2-product-form');
  const controller = component && init(component);
  if (!controller) return;
  const operation = pendingProducts.get(controller.key);
  if (operation) {
    const snapshots = operation.fields.get(controller) || [];
    for (const snapshot of snapshots) snapshot.field.value = snapshot.value;
    stop(event);
    return;
  }
  // Existing Resin variant handlers update availability and quantity rules first.
  queueMicrotask(() => { if (!pendingProducts.has(controller.key)) reset(controller); });
}, true);

document.addEventListener('variant:update', (event) => {
  queueMicrotask(() => {
    const productId = String(event.detail?.data?.productId || '');
    document.querySelectorAll('.fr2-product-form').forEach((component) => {
      const controller = init(component);
      if (controller && (!productId || controller.key === productId) && !pendingProducts.has(controller.key)) reset(controller);
    });
  });
});

function start() {
  initWithin(document);
  new MutationObserver((records) => {
    for (const record of records) for (const node of record.addedNodes) {
      if (node.nodeType === 1) initWithin(node);
    }
  }).observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, { once: true });
else start();
document.addEventListener('shopify:section:load', (event) => initWithin(event.target));
